import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import { useEffect, useState } from 'react';
import { ConEdisonAccount } from '../profile/Profile';
import { Button } from '@shadcn/ui';
import { BoltIcon } from 'lucide-react';
import { CLIENT_ID, generateRandomState, getConEdAuthUrl } from 'app/utils/constants/conEdison';
import { ConEdisonResidentialAnalysis } from '../project/tabs/analysis/ConEdisonResidentialAnalysis';
import type { ProjectInfoData } from 'app/services/project-services';
import projectServices from 'app/services/project-services';

export const ResidentialHome = () => {
  const { authUser } = useJumboAuth();
  const [conEdisonAccounts, setConEdAccounts] = useState<ConEdisonAccount[]>([]);
  const [project, setProject] = useState<ProjectInfoData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (authUser?.relationships?.organization?.data?.conEdisonAccounts) {
        setConEdAccounts(authUser.relationships.organization.data.conEdisonAccounts);
        const projects = await projectServices.get({ limit: 1 });
        setProject(projects.data[0]);
      }
    };
    fetchData();
  }, [authUser]);

  const initiateConEdConnection = () => {
    // Validate configuration
    if (!CLIENT_ID) {
      console.error('ConEd integration not properly configured');
      return;
    }

    try {
      // Generate random state for security
      const state = generateRandomState();

      // Store state in session for verification later
      sessionStorage.setItem('coned_state', state);

      // Redirect user to ConEd
      window.location.href = getConEdAuthUrl(state);
    } catch (error) {
      console.error('Error initiating ConEd connection:', error);
    }
  };

  const hasActiveAccount = conEdisonAccounts.some((account) => account.status !== 'inactive');

  return (
    <div className="grid min-h-screen grid-rows-[1fr_auto]">
      <div className="flex flex-col items-center justify-center p-8">
        {(!conEdisonAccounts || conEdisonAccounts.length === 0 || !hasActiveAccount) && (
          <div className="flex flex-col items-center space-y-4 text-center">
            <div className="rounded-full bg-primary/10 p-4">
              <BoltIcon className="h-12 w-12 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Connect to ConEdison</h2>
            <p className="max-w-md text-muted-foreground">
              To view your energy usage and insights, please connect your ConEdison account.
            </p>
            <Button
              onClick={initiateConEdConnection}
              size="lg"
              className="mt-4 bg-primary/90 text-primary-foreground hover:bg-primary"
            >
              <BoltIcon className="mr-2 h-5 w-5" />
              Connect ConEdison Account
            </Button>
          </div>
        )}
        {conEdisonAccounts && conEdisonAccounts.length > 0 && hasActiveAccount && project && (
          <div className="w-full">
            <ConEdisonResidentialAnalysis project={project} />
          </div>
        )}
      </div>
    </div>
  );
};
